import React, { useState, useEffect } from "react";

// Supabase
import { supabase } from "../../supabase";

// Context
import { useAuth } from "../../contexts/AuthContext";

// React Icons
import {
    FaCheckSquare,
    FaSortAmountDownAlt,
    FaFilter,
} from "react-icons/fa";

// Components
import JobsTableLoader from "./components/JobsTableLoader";


const JobApplicationsTrackerPage = () => {
    const { user } = useAuth();

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });
    const [allApplications, setAllApplications] = useState([]);
    const [selectedApplications, setSelectedApplications] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'internal' | 'external'
    const [loading, setLoading] = useState(true);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const getInternalStatusLabel = (status) => {
        if (!status) return 'Applied';
        if (status === 'screening') return 'Shortlist';
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    useEffect(() => {
        if (!user) return;
        fetchAllApplications();
    }, [user]);

    const fetchAllApplications = async () => {
        try {
            setLoading(true);
            
            // Fetch external applications
            const { data: externalData, error: externalError } = await supabase
                .from("my_applications")
                .select(
                    `
                    *,
                    jobs:job_id (
                        id,
                        company,
                        location,
                        continent,
                        title,
                        fulltime_parttime,
                        experience,
                        category,
                        deadline,
                        application_url,
                        created_at,
                        last_updated,
                        admin_notes,
                        live,
                        date_added,
                        sheet_row
                    )
                `
                )
                .eq("user_id", user.id)
                .neq("status", "bookmarked")
                .order("created_at", { ascending: false });

            if (externalError) throw externalError;

            // Fetch internal applications
            const { data: internalData, error: internalError } = await supabase
                .from('applications')
                .select(`
                    *,
                    internal_job_listings!job_listing_id(
                        id,
                        title,
                        company_name,
                        location,
                        employment_type,
                        created_at
                    )
                `)
                .eq('user_id', user.id)
                .order('application_date', { ascending: false });

            if (internalError) throw internalError;

            // Transform external applications to unified format
            const externalFormatted = (externalData || [])
                .filter(item => item.jobs) // Filter out any items where job is null
                .map((item) => ({
                    // Distinct UI id, plus the Supabase row id for mutations
                    id: `external-${item.id}`,
                    applicationId: item.id, // primary key in my_applications
                    type: 'external',
                    title: item.jobs.title,
                    company: item.jobs.company,
                    location: item.jobs.location,
                    continent: item.jobs.continent,
                    fulltime_parttime: item.jobs.fulltime_parttime,
                    experience: item.jobs.experience,
                    category: item.jobs.category,
                    deadline: item.jobs.deadline,
                    application_url: item.jobs.application_url,
                    created_at: item.jobs.created_at,
                    application_date: item.created_at,
                    status: item.status || 'bookmarked',
                    admin_notes: item.jobs.admin_notes,
                }));

            // Transform internal applications to unified format
            // Filter out applications where the job listing is missing (similar to external applications)
            const internalFormatted = (internalData || [])
                .filter(app => app.internal_job_listings) // Filter out applications with missing job listings
                .map(app => ({
                    // Distinct UI id, plus the Supabase row id for mutations
                    id: `internal-${app.id}`,
                    applicationId: app.id, // primary key in applications
                    type: 'internal',
                    title: app.internal_job_listings?.title,
                    company: app.internal_job_listings?.company_name,
                    location: app.internal_job_listings?.location,
                    continent: null,
                    fulltime_parttime: app.internal_job_listings?.employment_type,
                    experience: null,
                    category: null,
                    deadline: null,
                    application_url: null,
                    created_at: app.internal_job_listings?.created_at,
                    application_date: app.application_date,
                    status: app.status || 'bookmarked', // Default to 'bookmarked' if status is missing
                    admin_notes: null,
                }));

            // Combine both types
            const combined = [...externalFormatted, ...internalFormatted];
            setAllApplications(combined);
        } catch (error) {
            console.error("Error fetching applications:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (applicationId, newStatus) => {
        try {
            const application = allApplications.find((app) => app.id === applicationId);
            if (!application) {
                return;
            }

            // Update local state immediately
            setAllApplications((prevApps) =>
                prevApps.map((app) =>
                    app.id === applicationId ? { ...app, status: newStatus } : app
                )
            );

            // Update in database based on type
            if (application.type === 'external') {
                const { data, error } = await supabase
                    .from("my_applications")
                    .update({
                        status: newStatus,
                        updated_at: new Date().toISOString(),
                    })
                    .eq("id", application.applicationId)
                    .select();

                if (error) {
                    console.error("Database error updating external application status:", error);
                    console.error("Error details:", {
                        message: error.message,
                        details: error.details,
                        hint: error.hint,
                        code: error.code
                    });
                    throw error;
                }
                
                // Refresh to ensure UI is in sync with database
                await fetchAllApplications();
            } else {
                // Internal application
                const { error } = await supabase
                    .from('applications')
                    .update({ 
                        status: newStatus, 
                        last_updated: new Date().toISOString() 
                    })
                    .eq('id', application.applicationId);

                if (error) throw error;
                
                // Refresh to ensure UI is in sync with database
                await fetchAllApplications();
            }
        } catch (error) {
            console.error("Error updating status:", error);
            // Refresh on error to revert optimistic update
            fetchAllApplications();
        }
    };

    const handleDelete = async () => {
        if (selectedApplications.length === 0) return;

        // Optional safety confirmation so users don't remove applications by mistake
        const confirmed = window.confirm(
            `Are you sure you want to delete ${selectedApplications.length} application(s) from your tracker?`
        );
        if (!confirmed) return;

        try {
            const applicationsToDelete = allApplications.filter((app) =>
                selectedApplications.includes(app.id)
            );

            // Separate external and internal applications
            const externalIds = applicationsToDelete
                .filter(app => app.type === 'external')
                .map(app => app.applicationId);

            const internalIds = applicationsToDelete
                .filter(app => app.type === 'internal')
                .map(app => app.applicationId);

            // Delete external applications
            if (externalIds.length > 0) {
                const { error: externalError } = await supabase
                    .from("my_applications")
                    .delete()
                    .in("id", externalIds);

                if (externalError) throw externalError;
            }

            // Delete internal applications
            if (internalIds.length > 0) {
                const { error: internalError } = await supabase
                    .from('applications')
                    .delete()
                    .in('id', internalIds);

                if (internalError) throw internalError;
            }

            // Always refetch from Supabase so that when you come back,
            // the deleted applications stay gone.
            await fetchAllApplications();
            setSelectedApplications([]);
        } catch (error) {
            console.error("Error deleting applications:", error);
            // Refresh to keep UI in sync even if something went wrong
            fetchAllApplications();
        }
    };

    // Get all possible statuses from both types
    const allPossibleStatuses = [
        'applied', // both
        'screening', // internal
        'interview', // internal
        'assessment', // internal
        'offer', // both
        'hired', // internal
        'rejected', // both
        'no responses' // both
    ];
    const statusOptions = ["all", ...allPossibleStatuses];

    // Filter by type and status
    const filteredApplications = allApplications.filter((app) => {
        const matchesType = typeFilter === "all" || app.type === typeFilter;
        const matchesStatus = statusFilter === "all" || app.status === statusFilter;
        return matchesType && matchesStatus;
    });

    // Sort applications
    const sortedApplications = [...filteredApplications].sort((a, b) => {
        if (!sortConfig.key) {
            // Default sort by application_date (newest first)
            const aDate = new Date(a.application_date || a.created_at);
            const bDate = new Date(b.application_date || b.created_at);
            return sortConfig.direction === "asc" 
                ? aDate - bDate 
                : bDate - aDate;
        }

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
            return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
    });

    const handleSelectAll = () => {
        if (selectedApplications.length === sortedApplications.length) {
            setSelectedApplications([]);
        } else {
            setSelectedApplications(sortedApplications.map((app) => app.id));
        }
    };

    const handleApplicationSelection = (applicationId) => {
        setSelectedApplications((prev) =>
            prev.includes(applicationId)
                ? prev.filter((id) => id !== applicationId)
                : [...prev, applicationId]
        );
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-black">
                    Please log in or sign up to view your applications
                </div>
            </div>
        );
    }

    const getStatusOptions = (type) => {
        if (type === 'internal') {
            return [ 'applied', 'screening', 'interview', 'assessment', 'offer', 'hired', 'rejected', 'no responses'];
        } else {
            // External applications: bookmarked -> applied -> interview -> offer -> no responses -> rejected
            return ['applied', 'interview', 'offer', 'no responses', 'rejected'];
        }
    };

    const getStatusLabel = (status, type) => {
        if (type === 'internal') {
            return getInternalStatusLabel(status);
        }
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Applied';
    };

    const getStatusClass = (status) => {
        switch (status) {
            case "applied":
                return "bg-blue-100 text-blue-800";
            case "screening":
                return "bg-purple-100 text-purple-800";
            case "interview":
                return "bg-indigo-100 text-indigo-800";
            case "assessment":
                return "bg-yellow-100 text-yellow-800";
            case "offer":
                return "bg-green-100 text-green-800";
            case "hired":
                return "bg-emerald-100 text-emerald-800";
            case "no responses":
                return "bg-yellow-100 text-yellow-800";
            case "rejected":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (loading) {
        return <JobsTableLoader />;
    }

    return (
        <>
        <style>{`
            @media (max-width: 1280px) {
                .job-applications-container {
                    padding: 1rem !important;
                }
                .job-applications-container h2 {
                    font-size: 1.25rem !important;
                    margin-bottom: 1rem !important;
                }
                .job-applications-filters {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.75rem !important;
                }
                .job-applications-filters > div:first-child {
                    width: 100% !important;
                }
                .job-applications-filters-inner {
                    display: none !important;
                }
                .job-applications-filters-desktop {
                    display: none !important;
                }
                .job-applications-mobile-filter-button {
                    display: flex !important;
                    width: 100% !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                    padding: 0.75rem 1rem !important;
                    border: 2px solid #d1d5db !important;
                    border-radius: 9999px !important;
                    background: white !important;
                    font-size: 0.875rem !important;
                    font-weight: 500 !important;
                    color: #111827 !important;
                }
                .job-applications-mobile-filter-dropdown {
                    display: none !important;
                    position: absolute !important;
                    top: 100% !important;
                    left: 0 !important;
                    right: 0 !important;
                    margin-top: 0.5rem !important;
                    background: white !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 0.5rem !important;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
                    z-index: 50 !important;
                    padding: 1rem !important;
                    gap: 1rem !important;
                }
                .job-applications-mobile-filter-dropdown.show {
                    display: flex !important;
                    flex-direction: column !important;
                }
                .job-applications-mobile-filter-group {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 0.5rem !important;
                }
                .job-applications-mobile-filter-group label {
                    font-size: 0.75rem !important;
                    font-weight: 600 !important;
                    color: #374151 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                }
                .job-applications-mobile-filter-group select {
                    width: 100% !important;
                    padding: 0.5rem 0.75rem !important;
                    border: 1px solid #d1d5db !important;
                    border-radius: 0.375rem !important;
                    font-size: 0.875rem !important;
                    background: white !important;
                }
                .job-applications-mobile-filter-actions {
                    display: flex !important;
                    gap: 0.5rem !important;
                    margin-top: 0.5rem !important;
                }
                .job-applications-mobile-filter-actions button {
                    flex: 1 !important;
                    padding: 0.5rem !important;
                    border-radius: 0.375rem !important;
                    font-size: 0.875rem !important;
                }
                .job-applications-table {
                    overflow-x: auto !important;
                    -webkit-overflow-scrolling: touch !important;
                }
                .job-applications-table-header {
                    display: none !important;
                }
                .job-applications-table-row {
                    display: flex !important;
                    flex-direction: column !important;
                    padding: 1rem !important;
                    gap: 0.75rem !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    background: white !important;
                    border-radius: 0.5rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .job-applications-table-row > div {
                    display: none !important;
                }
                .job-applications-table-row .job-applications-mobile-card {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 0.5rem !important;
                }
                .job-applications-mobile-card-title {
                    font-weight: 600 !important;
                    font-size: 0.875rem !important;
                    color: #111827 !important;
                    margin-bottom: 0.25rem !important;
                }
                .job-applications-mobile-card-company {
                    font-size: 0.75rem !important;
                    color: #374151 !important;
                }
                .job-applications-mobile-card-location {
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                }
                .job-applications-mobile-card-date {
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                }
                .job-applications-mobile-card-status {
                    margin-top: 0.5rem !important;
                }
                .job-applications-mobile-card-status select {
                    width: 100% !important;
                    padding: 0.5rem !important;
                    font-size: 0.75rem !important;
                }
            }
        `}</style>
        <div className="flex flex-col min-h-screen bg-white">
            <main className="main-content">
                <div className="content-container job-applications-container">
                    <div className="flex md:flex-row flex-col items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                            <h2 className="text-2xl font-bold">My Applications</h2>
                        </div>
                    </div>

                    {/* Filters and Actions */}
                    <div className="flex md:flex-row flex-col items-center justify-between mb-6 gap-4 job-applications-filters">
                        {/* Mobile Filter Button */}
                        <div className="relative w-full xl:hidden">
                            <button
                                className="job-applications-mobile-filter-button"
                                onClick={() => setShowMobileFilters(!showMobileFilters)}
                            >
                                <span className="flex items-center gap-2">
                                    <FaFilter className="text-base" />
                                    Filters
                                    {(typeFilter !== "all" || statusFilter !== "all") && (
                                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                            {[typeFilter !== "all" ? 1 : 0, statusFilter !== "all" ? 1 : 0].reduce((a, b) => a + b, 0)}
                                        </span>
                                    )}
                                </span>
                                <svg
                                    className={`w-5 h-5 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div className={`job-applications-mobile-filter-dropdown ${showMobileFilters ? 'show' : ''}`}>
                                <div className="job-applications-mobile-filter-group">
                                    <label>Type</label>
                                    <select
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="external">External</option>
                                        <option value="internal">Internal</option>
                                    </select>
                                </div>
                                <div className="job-applications-mobile-filter-group">
                                    <label>Status</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        disabled={allApplications.length === 0}
                                    >
                                        {statusOptions.map((status) => {
                                            let label = status;
                                            if (status === "all") {
                                                label = "All Statuses";
                                            } else if (status === "screening") {
                                                label = "Shortlist";
                                            } else {
                                                label = status.charAt(0).toUpperCase() + status.slice(1);
                                            }
                                            return (
                                                <option key={status} value={status}>
                                                    {label}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="job-applications-mobile-filter-group">
                                    <label>Sort</label>
                                    <select
                                        value={sortConfig.direction}
                                        onChange={(e) => setSortConfig(prev => ({
                                            ...prev,
                                            direction: e.target.value
                                        }))}
                                    >
                                        <option value="desc">Newest First</option>
                                        <option value="asc">Oldest First</option>
                                    </select>
                                </div>
                                <div className="job-applications-mobile-filter-actions">
                                    <button
                                        className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        onClick={() => setShowMobileFilters(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Desktop Filters */}
                        <div className="hidden xl:flex items-center gap-4 flex-wrap md:flex-nowrap job-applications-filters-desktop">
                            <div className="flex items-center gap-2 flex-wrap job-applications-filters-inner">
                                <div className="job-applications-filter-item">
                                    <button
                                        className={`px-3 py-1 rounded-full border-2 ${
                                            selectedApplications.length > 0
                                                ? "border-blue-500 text-blue-500"
                                                : "border-gray-300 text-black"
                                        } flex items-center gap-1 hover:bg-gray-50`}
                                        onClick={handleSelectAll}
                                        disabled={allApplications.length === 0}
                                        style={{
                                            opacity: allApplications.length === 0 ? 0.5 : 1,
                                        }}
                                    >
                                        <FaCheckSquare className="text-base" />
                                        Selected {selectedApplications.length > 0 && `(${selectedApplications.length})`}
                                    </button>
                                </div>

                                <div className="relative job-applications-filter-item">
                                    <div
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 ${
                                            typeFilter !== "all"
                                                ? "border-blue-500 text-blue-500"
                                                : "border-gray-300 text-black"
                                        }`}
                                    >
                                        <FaFilter className="text-base" />
                                        <select
                                            className="bg-transparent border-none focus:outline-none text-inherit cursor-pointer"
                                            value={typeFilter}
                                            onChange={(e) => setTypeFilter(e.target.value)}
                                        >
                                            <option value="all">All Types</option>
                                            <option value="external">External</option>
                                            <option value="internal">Internal</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="relative job-applications-filter-item">
                                    <div
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 ${
                                            statusFilter !== "all"
                                                ? "border-blue-500 text-blue-500"
                                                : "border-gray-300 text-black"
                                        }`}
                                    >
                                        <FaFilter className="text-base" />
                                        <select
                                            className="bg-transparent border-none focus:outline-none text-inherit cursor-pointer"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            disabled={allApplications.length === 0}
                                        >
                                            {statusOptions.map((status) => {
                                                let label = status;
                                                if (status === "all") {
                                                    label = "All Statuses";
                                                } else if (status === "screening") {
                                                    label = "Shortlist";
                                                } else {
                                                    label = status.charAt(0).toUpperCase() + status.slice(1);
                                                }
                                                return (
                                                    <option key={status} value={status}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>

                                <div className="job-applications-filter-item">
                                    <button
                                        className={`px-3 py-1 rounded-full border ${
                                            selectedApplications.length > 0
                                                ? "border-red-500 text-red-500 hover:bg-red-50"
                                                : "border-gray-300 text-gray-400"
                                        } flex items-center gap-1`}
                                        onClick={handleDelete}
                                        disabled={selectedApplications.length === 0}
                                    >
                                        <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" /> Delete {selectedApplications.length > 0 && `(${selectedApplications.length})`}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="relative job-applications-filters-desktop">
                            <button 
                                className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 flex items-center gap-1"
                                onClick={() => setSortConfig(prev => ({
                                    key: null,
                                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                                }))}
                            >
                                Sort by <FaSortAmountDownAlt className="text-base" />
                                <span className="ml-1">{sortConfig.direction === 'asc' ? 'Oldest' : 'Newest'}</span>
                            </button>
                        </div>
                    </div>

                    {/* Unified Table */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden w-full job-applications-table">
                        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 job-applications-table-header">
                            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                                <div className="col-span-1"></div>
                                <div className="col-span-3">Job</div>
                                <div className="col-span-2">Company</div>
                                <div className="col-span-2">Location</div>
                                <div className="col-span-2">Applied Date</div>
                                <div className="col-span-2">Status</div>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {sortedApplications.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-600">
                                    No applications found. {typeFilter !== "all" && `Try adjusting your filters.`}
                                </div>
                            ) : (
                                sortedApplications.map(app => (
                                    <div key={app.id} className="px-6 py-4 hover:bg-gray-50 job-applications-table-row">
                                        {/* Desktop Grid View */}
                                        <div className="grid-cols-12 gap-4 items-center hidden sm:grid">
                                            <div className="col-span-1">
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    checked={selectedApplications.includes(app.id)}
                                                    onChange={() => handleApplicationSelection(app.id)}
                                                />
                                            </div>
                                            <div className="col-span-3 text-sm text-gray-900 font-medium">{app.title || 'N/A'}</div>
                                            <div className="col-span-2 text-sm text-gray-700">{app.company || 'N/A'}</div>
                                            <div className="col-span-2 text-sm text-gray-600">{app.location || 'N/A'}</div>
                                            <div className="col-span-2 text-sm text-gray-600">
                                                {app.application_date 
                                                    ? new Date(app.application_date).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })
                                                    : 'N/A'
                                                }
                                            </div>
                                            <div className="col-span-2">
                                                <select
                                                    value={app.status}
                                                    onChange={(e) => handleStatusChange(app.id, e.target.value)}
                                                    disabled={app.type === 'internal'}
                                                    className={`text-xs font-medium px-2 py-1 rounded border-0 focus:ring-1 focus:ring-blue-500 ${
                                                        app.type === 'internal'
                                                            ? 'cursor-not-allowed opacity-100' 
                                                            : 'cursor-pointer'
                                                    } ${getStatusClass(app.status)}`}
                                                    style={app.type === 'internal' ? { opacity: 1 } : {}}
                                                >
                                                    {getStatusOptions(app.type).map(s => (
                                                        <option key={s} value={s}>
                                                            {app.type === 'internal' && s === 'screening' ? 'Shortlist' : s.charAt(0).toUpperCase() + s.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        {/* Mobile Card View */}
                                        <div className="sm:hidden job-applications-mobile-card">
                                            <div className="job-applications-mobile-card-title">{app.title || 'N/A'}</div>
                                            <div className="job-applications-mobile-card-company">{app.company || 'N/A'}</div>
                                            <div className="job-applications-mobile-card-location">{app.location || 'N/A'}</div>
                                            <div className="job-applications-mobile-card-date">
                                                Applied: {app.application_date 
                                                    ? new Date(app.application_date).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })
                                                    : 'N/A'
                                                }
                                            </div>
                                            <div className="job-applications-mobile-card-status">
                                                <select
                                                    value={app.status}
                                                    onChange={(e) => handleStatusChange(app.id, e.target.value)}
                                                    disabled={app.type === 'internal'}
                                                    className={`text-xs font-medium px-2 py-1 rounded border-0 focus:ring-1 focus:ring-blue-500 w-full ${
                                                        app.type === 'internal'
                                                            ? 'cursor-not-allowed opacity-100' 
                                                            : 'cursor-pointer'
                                                    } ${getStatusClass(app.status)}`}
                                                    style={app.type === 'internal' ? { opacity: 1 } : {}}
                                                >
                                                    {getStatusOptions(app.type).map(s => (
                                                        <option key={s} value={s}>
                                                            {app.type === 'internal' && s === 'screening' ? 'Shortlist' : s.charAt(0).toUpperCase() + s.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
        </>
    );
};

export default JobApplicationsTrackerPage;
